import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { MovelListItem, MovelMeta, PlantaMeta } from '@visualizador/shared';
import { api } from '../api/client.js';
import { PainelDesempenho } from '../components/PainelDesempenho.js';
import { BarraMedir } from '../components/BarraMedir.js';
import { BussolaEixos } from '../components/BussolaEixos.js';
import { lerBolinhaFixa } from '../lib/medir.js';
import {
  adicionarOculta, chaveDaPeca, chaveDoCliente, idsParaEsconder, lerOcultas, limparOcultas,
  ocultasPresentes, rotuloExibirTudo,
} from '../lib/ocultas.js';
import { ListaFolgas } from '../components/ListaFolgas.js';
import { MovelViewer, type ViewerInfo, type PieceMenu, type EstadoMedir, type EixosTela, type Folga } from '../three/viewerCore.js';
import { getTheme, sceneBg } from '../lib/theme.js';
import { materialParaExibir } from '../lib/material.js';
import { useSenhaAdmin } from '../components/SenhaAdmin.js';

/**
 * "Casa montada": abre TODOS os moveis de um cliente (arquivo do SketchUp) na
 * mesma cena, cada um na posicao/altura real do projeto.
 *
 * Como o plugin grava a geometria em coordenadas do MUNDO, os moveis encaixam
 * sozinhos — nao existe offset a aplicar. Para nao travar, o conteudo entra UM
 * POR VEZ, com indicador de progresso: primeiro a PLANTA (o piso, que serve de
 * referencia), depois os moveis em sequencia.
 *
 * Reaproveita as classes `.vw-*` do viewer de um movel — inclusive a gaveta do
 * celular — para ter a mesma barra lateral (medidas, lista de pecas) sem
 * duplicar CSS nem tocar no ViewerPage.
 */

type Estado =
  | { status: 'lista' }
  | { status: 'planta' }
  | { status: 'montando'; carregados: number; total: number; atual: string }
  | { status: 'pronto'; total: number; falhas: string[] }
  | { status: 'erro'; texto: string };

interface Grupo {
  movel_id: string;
  nome: string;
  pieces: MovelMeta['pieces'];
  /** Materiais do arquivo: resolvem o nome quando a peca so tem material_id. */
  materiais: MovelMeta['materiais'];
}


export function ProjetoPage() {
  const [params] = useSearchParams();
  const cliente = (params.get('cliente') ?? '').trim();

  const canvasRef = useRef<HTMLDivElement | null>(null);
  const viewerRef = useRef<MovelViewer | null>(null);
  // ?debug=1 mostra o painel de desempenho (medir no tablet de verdade).
  const [viewerDebug, setViewerDebug] = useState<MovelViewer | null>(null);
  const [debug] = useState(() => new URLSearchParams(window.location.search).get('debug') === '1');
  const ctxRef = useRef<HTMLDivElement | null>(null);

  const [estado, setEstado] = useState<Estado>({ status: 'lista' });
  const [info, setInfo] = useState<ViewerInfo>(null);
  // Folgas da peça selecionada até as vizinhas (as 6 direções) — ver ListaFolgas.
  const [folgas, setFolgas] = useState<Folga[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [moveis, setMoveis] = useState<MovelListItem[]>([]);
  const [grupos, setGrupos] = useState<Grupo[]>([]);
  const [aberto, setAberto] = useState<string | null>(null); // grupo expandido
  const [filtro, setFiltro] = useState('');
  const [menuAberto, setMenuAberto] = useState(false);
  const [pieceMenu, setPieceMenu] = useState<PieceMenu | null>(null);
  // Peças ocultadas à mão e gravadas neste navegador (chaves do lib/ocultas).
  // Aqui a chave é do CLIENTE: a casa montada é uma tela só, com todos os móveis.
  const [ocultas, setOcultas] = useState<string[]>([]);
  // Casa inteira comeca SEM ferragens: medido, eram 99,6% dos triangulos da cena.
  const [ferragensVisiveis, setFerragensVisiveis] = useState(false);
  // [carregadas, total] enquanto baixa as ferragens sob demanda.
  const [progressoFerragens, setProgressoFerragens] = useState<[number, number] | null>(null);

  const [medindo, setMedindo] = useState(false);
  // Direção de X/Y/Z na tela (bússola do medir); o viewer só avisa medindo.
  const [eixosTela, setEixosTela] = useState<EixosTela | null>(null);
  const [estadoMedir, setEstadoMedir] = useState<EstadoMedir>(() => ({
    fixa: lerBolinhaFixa(), mira: false, pendente: false, distancia: '', medidas: 0,
    encaixe: '', eixo: 'livre', componentes: '',
  }));
  const [medidasVisiveis, setMedidasVisiveis] = useState(true);

  const [planta, setPlanta] = useState<PlantaMeta | null>(null);
  // Ajustes da planta ficam recolhidos: raramente usados e ocupavam meia barra.
  const [plantaAberta, setPlantaAberta] = useState(false);
  const [plantaVisivel, setPlantaVisivel] = useState(true);
  const [opacidade, setOpacidade] = useState(0.85);
  const [larguraM, setLarguraM] = useState('');
  const [rotacao, setRotacao] = useState('');
  const [alturaY, setAlturaY] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [modalAdmin, pedirAdmin] = useSenhaAdmin();

  // Cria o viewer 3D uma vez.
  useEffect(() => {
    if (!canvasRef.current) return;
    const viewer = new MovelViewer(canvasRef.current, {
      onInfo: setInfo,
      onFolgas: setFolgas,
      onSelected: setSelectedId,
      onPieceMenu: (m) => setPieceMenu(m),
      onFerragens: setFerragensVisiveis,
      onFerragensProgresso: (feitas, total) => setProgressoFerragens(total ? [feitas, total] : null),
      onMedir: setEstadoMedir,
      onEixosTela: setEixosTela,
    });
    // Antes de carregar: assim a geometria das ferragens nem sobe para a GPU.
    viewer.setFerragensVisiveis(false);
    viewer.setBolinhaFixa(lerBolinhaFixa());
    viewerRef.current = viewer;
    setViewerDebug(viewer);
    viewer.setBackground(sceneBg(getTheme()));
    return () => { viewer.dispose(); viewerRef.current = null; };
  }, []);

  useEffect(() => {
    const sync = () => viewerRef.current?.setBackground(sceneBg(getTheme()));
    window.addEventListener('themechange', sync);
    return () => window.removeEventListener('themechange', sync);
  }, []);

  // Menu de contexto (botao direito): fecha ao clicar fora.
  useEffect(() => {
    if (!pieceMenu) return;
    const onDown = (e: PointerEvent) => {
      if (ctxRef.current && !ctxRef.current.contains(e.target as Node)) setPieceMenu(null);
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [pieceMenu]);

  // Carrega: lista -> PLANTA (primeiro) -> moveis, um por vez.
  useEffect(() => {
    if (!cliente) { setEstado({ status: 'erro', texto: 'Cliente não informado.' }); return; }

    let cancelado = false;
    setEstado({ status: 'lista' });

    (async () => {
      try {
        const first = await api.listMoveis('', 1, 100);
        let todos = first.items;
        const paginas = Math.ceil((first.total || 0) / 100);
        for (let p = 2; p <= paginas && p <= 50; p++) {
          const r = await api.listMoveis('', p, 100);
          todos = todos.concat(r.items);
        }
        if (cancelado) return;

        const doCliente = todos.filter((m) => (m.arquivo || '').trim() === cliente && m.hasGlb);
        setMoveis(doCliente);
        if (doCliente.length === 0) {
          setEstado({ status: 'erro', texto: 'Nenhum móvel com modelo 3D para este cliente.' });
          return;
        }

        const esperarViewer = async (): Promise<MovelViewer> => {
          for (let i = 0; i < 100; i++) {
            if (viewerRef.current) return viewerRef.current;
            await new Promise((r) => setTimeout(r, 50));
          }
          throw new Error('viewer 3D nao inicializou');
        };
        const viewer = await esperarViewer();
        if (cancelado) return;
        viewer.clearScene();
        viewer.clearPlanta();

        // 1) A PLANTA primeiro: ela e o piso, a referencia de onde tudo assenta.
        setEstado({ status: 'planta' });
        const pl = await api.getPlanta(cliente);
        if (cancelado) return;
        if (pl) {
          await aplicarPlanta(viewer, pl).catch(() => undefined);
          if (cancelado) return;
        }

        // 2) Depois os moveis, um por vez.
        const falhas: string[] = [];
        for (let i = 0; i < doCliente.length; i++) {
          if (cancelado) return;
          const m = doCliente[i]!;
          const nome = m.nome || m.movel_id;
          setEstado({ status: 'montando', carregados: i, total: doCliente.length, atual: nome });
          try {
            // So a MADEIRA agora; as ferragens ficam registradas e so sao baixadas
            // se a pessoa apertar "Mostrar ferragens" (eram ~98% do download).
            await viewer.appendURL(
              api.glbUrl(m.movel_id, m.arquivo, m.atualizado_em),
              nome,
              m.ferragensSeparadas ? api.glbUrl(m.movel_id, m.arquivo, m.atualizado_em, 'ferragens') : undefined,
            );
          } catch {
            falhas.push(nome);
          }
          // Metadados (nomes/materiais) alimentam a lista da barra lateral.
          api.getMovel(m.movel_id, m.arquivo).then((meta) => {
            if (cancelado || !meta) return;
            setGrupos((g) => (g.some((x) => x.movel_id === meta.movel_id)
              ? g
              : [...g, {
                movel_id: meta.movel_id,
                nome: meta.nome || meta.movel_id,
                pieces: meta.pieces,
                materiais: meta.materiais ?? [],
              }]));
          }).catch(() => undefined);
          await new Promise((r) => setTimeout(r, 0));
        }
        if (cancelado) return;
        const ok = doCliente.length - falhas.length;
        if (ok === 0) {
          setEstado({ status: 'erro', texto: 'Nenhum modelo 3D deste cliente pôde ser carregado.' });
          return;
        }
        setEstado({ status: 'pronto', total: ok, falhas });
      } catch {
        if (!cancelado) setEstado({ status: 'erro', texto: 'Erro ao carregar o projeto.' });
      }
    })();

    return () => { cancelado = true; };
  }, [cliente]);

  async function aplicarPlanta(viewer: MovelViewer, pl: PlantaMeta) {
    const p = pl.posicionamento;
    await viewer.setPlanta({
      url: api.plantaImagemUrl(pl.arquivo, pl.atualizado_em),
      centro: p.centro,
      largura_mm: p.largura_mm,
      altura_mm: p.altura_mm,
      rotacao_deg: p.rotacao_deg,
      espelhada: p.espelhada,
      opacidade: pl.opacidade,
      visivel: pl.visivel,
    });
    setPlanta(pl);
    setPlantaVisivel(pl.visivel);
    setOpacidade(pl.opacidade);
    setLarguraM((p.largura_mm / 1000).toFixed(2));
    setRotacao(String(p.rotacao_deg));
    setAlturaY(String(Math.round(p.centro.y)));
  }

  // Chave deste cliente no armazenamento das peças ocultas.
  const chaveOcultas = chaveDoCliente(cliente);

  // Devolve ao viewer as peças que a pessoa tinha escondido antes de recarregar.
  // Roda a cada móvel cujos metadados chegam: só com eles dá para traduzir o
  // caminho_pid gravado para o id que o 3D entende.
  useEffect(() => {
    const v = viewerRef.current;
    if (!v || grupos.length === 0) return;
    const gravadas = lerOcultas(chaveOcultas);
    const pecas = grupos.flatMap((g) => g.pieces);
    setOcultas(ocultasPresentes(gravadas, pecas));
    for (const id of idsParaEsconder(gravadas, pecas)) v.hidePiece(id);
  }, [grupos, chaveOcultas]);

  // Grava uma peça como oculta (pelo caminho_pid quando existe — ver lib/ocultas).
  function gravarOcultaDaPeca(id: string) {
    const peca = pecaPorId.get(id);
    setOcultas(adicionarOculta(chaveOcultas, peca ? chaveDaPeca(peca) : id));
  }

  // ---- acoes da barra ----
  function alternarMedir() {
    const novo = !medindo;
    setMedindo(novo);
    viewerRef.current?.setPointMeasureMode(novo);
    // Tablet/celular: a gaveta cobre o 3D; a barra de medir fica sobre a cena.
    if (novo) setMenuAberto(false);
  }
  function alternarMedidas() {
    const novo = !medidasVisiveis;
    setMedidasVisiveis(novo);
    viewerRef.current?.setMeasuresVisible(novo);
  }
  // "Exibir tudo" aqui NAO mexe nas ferragens de proposito: a casa inteira tem
  // ~48 MB delas, e a tela de projeto precisa continuar leve. Quem as baixa e o
  // botao "Mostrar ferragens", que so existe nesta tela.
  function exibirTudo() {
    viewerRef.current?.clearSelection();
    viewerRef.current?.clearManualHidden();
    // Esquece também o que estava gravado: senão a peça voltaria a sumir no
    // próximo carregamento e ninguém entenderia por quê.
    limparOcultas(chaveOcultas);
    setOcultas([]);
  }
  function ocultarPecaMenu() {
    if (pieceMenu) {
      viewerRef.current?.hidePiece(pieceMenu.pieceId);
      gravarOcultaDaPeca(pieceMenu.pieceId);
    }
    setPieceMenu(null);
  }
  // Pelo painel da peca (funciona sem mouse). Some a peca e sai do raio X.
  function ocultarSelecionada() {
    const v = viewerRef.current;
    if (!v || !selectedId) return;
    v.hidePiece(selectedId);
    gravarOcultaDaPeca(selectedId);
    v.clearSelection();
  }

  // ---- planta: apagar / ajustar ----
  // A planta vem SEMPRE do Prancha Mania (POST /api/planta). Nao existe upload
  // pelo site: se o cliente nao tem planta, o projeto abre so com os moveis.
  async function apagarPlanta() {
    if (!planta) return;
    if (!window.confirm('Apagar a planta deste cliente?\n\nIsso remove a imagem do servidor e não pode ser desfeito.')) return;
    let status = await api.apagarPlanta(cliente);
    if ((status === 401 || status === 403) && (await pedirAdmin())) status = await api.apagarPlanta(cliente);
    if (status === 401 || status === 403) return; // desistiu da senha de admin
    if (status < 200 || status >= 300) { window.alert('Não foi possível apagar a planta.'); return; }
    viewerRef.current?.clearPlanta();
    setPlanta(null);
  }

  async function salvarAjuste() {
    if (!planta) return;
    const largura = Number(larguraM.replace(',', '.')) * 1000;
    const rot = Number(rotacao.replace(',', '.'));
    const y = Number(alturaY.replace(',', '.'));
    if (!Number.isFinite(largura) || largura <= 0) { window.alert('Largura inválida.'); return; }

    // Mantem a proporcao da imagem ao mudar a largura.
    const p = planta.posicionamento;
    const razao = p.largura_mm > 0 ? p.altura_mm / p.largura_mm : 1;
    const novo = {
      centro: { x: p.centro.x, y: Number.isFinite(y) ? y : p.centro.y, z: p.centro.z },
      largura_mm: largura,
      altura_mm: largura * razao,
      rotacao_deg: Number.isFinite(rot) ? rot : p.rotacao_deg,
      espelhada: p.espelhada,
    };
    setSalvando(true);
    try {
      viewerRef.current?.ajustarPlanta(novo);
      const dto = { posicionamento: novo, opacidade, visivel: plantaVisivel };
      let r = await api.ajustarPlanta(cliente, dto);
      // Ajustar a planta muda o que TODOS veem: e so do administrador.
      if ((r.status === 401 || r.status === 403) && (await pedirAdmin())) r = await api.ajustarPlanta(cliente, dto);
      if (r.planta) setPlanta(r.planta);
    } finally {
      setSalvando(false);
    }
  }

  function mudarOpacidade(n: number) {
    setOpacidade(n);
    viewerRef.current?.setPlantaOpacidade(n);
  }
  function alternarPlantaVisivel() {
    const novo = !plantaVisivel;
    setPlantaVisivel(novo);
    viewerRef.current?.setPlantaVisivel(novo);
    // Mostrar/ocultar vale so para esta tela. Gravar para todos exige admin e e
    // feito no "Salvar ajuste" — aqui nao chamamos a API, senao funcionario
    // tomaria "sem permissao" so por esconder a planta para medir.
  }

  // Peca selecionada: nome, medida do projeto e MATERIAL vem dos metadados que
  // ja buscamos para montar a lista lateral (o GLB sozinho nao traz isso).
  const pecaPorId = useMemo(() => {
    const m = new Map<string, MovelMeta['pieces'][number] & { movel: string; materialTexto: string }>();
    for (const g of grupos) {
      for (const p of g.pieces) {
        m.set(p.id, { ...p, movel: g.nome, materialTexto: materialParaExibir(p, g.materiais) });
      }
    }
    return m;
  }, [grupos]);

  const pecaSel = selectedId ? pecaPorId.get(selectedId) : undefined;
  const dimsPecaReal = pecaSel && (pecaSel.dims.c || pecaSel.dims.l || pecaSel.dims.e)
    ? `${Math.round(pecaSel.dims.c)} × ${Math.round(pecaSel.dims.l)} × ${Math.round(pecaSel.dims.e)} mm`
    : '';

  // ---- lista de pecas (agrupada por movel: a casa toda tem ~1000 pecas) ----
  const gruposFiltrados = useMemo(() => {
    const q = filtro.trim().toLowerCase();
    if (!q) return grupos;
    return grupos
      .map((g) => ({ ...g, pieces: g.pieces.filter((p) => p.id.toLowerCase().includes(q) || (p.nome ?? '').toLowerCase().includes(q)) }))
      .filter((g) => g.pieces.length > 0);
  }, [grupos, filtro]);

  const progresso = estado.status === 'montando'
    ? Math.round((estado.carregados / Math.max(1, estado.total)) * 100)
    : 0;
  const primeiroChegou = estado.status === 'montando' ? estado.carregados > 0 : estado.status === 'pronto';

  if (estado.status === 'erro') {
    return (
      <div className="viewer-root">
        <div className="center-msg">
          <div className="error-box" style={{ maxWidth: 420 }}>{estado.texto}</div>
          <Link className="btn" to={cliente ? `/?cliente=${encodeURIComponent(cliente)}` : '/'} style={{ marginTop: 12 }}>← Voltar</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="vw">
      <button type="button" className="vw-menu-btn" aria-label="Abrir menu" onClick={() => setMenuAberto(true)}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <path d="M3 6h18M3 12h18M3 18h18" />
        </svg>
      </button>
      <div className={`vw-backdrop ${menuAberto ? 'aberto' : ''}`} role="presentation" onClick={() => setMenuAberto(false)} />

      <aside className={`vw-side ${menuAberto ? 'aberto' : ''}`}>
        <div className="vw-side-head">
          <Link to={`/?cliente=${encodeURIComponent(cliente)}`} className="btn vw-back">← Voltar</Link>
          <div className="vw-titulo">
            <div className="vw-nome">{cliente}</div>
            <div className="muted small">Projeto montado · {moveis.length} {moveis.length === 1 ? 'móvel' : 'móveis'}</div>
          </div>
          <button type="button" className="vw-side-close" aria-label="Fechar menu" onClick={() => setMenuAberto(false)}>✕</button>
        </div>

        {/* Planta baixa (piso) — recolhida; toca no titulo para ver os ajustes */}
        <div className="vw-resumo">
          <button type="button" className="vw-resumo-linha vw-planta-head" aria-expanded={plantaAberta}
            onClick={() => setPlantaAberta(!plantaAberta)}>
            <span className="vw-planta-titulo"><span className="vw-planta-seta">{plantaAberta ? '▾' : '▸'}</span>🗺 Planta baixa</span>
            <b>{planta ? (planta.origem === 'sketchup' ? 'do SketchUp' : 'enviada') : 'sem planta'}</b>
          </button>
          {!plantaAberta ? null : planta ? (
            <>
              <div className="row">
                <button type="button" className={`vw-frentes ${plantaVisivel ? 'on' : ''}`} onClick={alternarPlantaVisivel}>
                  {plantaVisivel ? '👁 Ocultar planta' : '🗺 Mostrar planta'}
                </button>
              </div>
              <label className="vw-lbl">
                Opacidade
                <input type="range" min="0.1" max="1" step="0.05" value={opacidade}
                  onChange={(e) => mudarOpacidade(Number(e.target.value))} />
              </label>
              <div className="vw-campos">
                <label>Largura (m)<input type="text" inputMode="decimal" value={larguraM} onChange={(e) => setLarguraM(e.target.value)} /></label>
                <label>Giro (°)<input type="text" inputMode="decimal" value={rotacao} onChange={(e) => setRotacao(e.target.value)} /></label>
                <label>Altura (mm)<input type="text" inputMode="decimal" value={alturaY} onChange={(e) => setAlturaY(e.target.value)} /></label>
              </div>
              <div className="vw-linha-frentes">
                <button type="button" className="vw-limpar" onClick={apagarPlanta}>🗑 Apagar</button>
                <button type="button" className="primary" disabled={salvando} onClick={salvarAjuste}>
                  {salvando ? 'Salvando…' : 'Salvar ajuste'}
                </button>
              </div>
            </>
          ) : (
            <div className="muted small">
              Este cliente ainda não tem planta. Publique a planta pelo Prancha Mania
              (ela entra sozinha aqui, já posicionada).
            </div>
          )}
        </div>

        <hr />

        <button type="button" className={`vw-toggle-medidas ${medidasVisiveis ? 'on' : ''}`} onClick={alternarMedidas}>
          {medidasVisiveis ? '🙈 Ocultar medidas' : '📏 Mostrar medidas'}
        </button>
        <button type="button" className={`vw-toggle-medidas ${medindo ? 'on' : ''}`} onClick={alternarMedir}>
          📐 {medindo ? 'Medindo… (toque para sair)' : 'Medir 2 pontos'}
        </button>
        <div className="vw-linha-frentes">
          <button type="button" className="vw-limpar" onClick={() => viewerRef.current?.clearFreeMeasures()}>Limpar</button>
          <button type="button" onClick={() => viewerRef.current?.resetCamera()}>Resetar câmera</button>
        </div>
        <button type="button" onClick={exibirTudo}>{rotuloExibirTudo(ocultas.length)}</button>
        <button type="button" className={`vw-frentes vw-ferragens ${ferragensVisiveis ? 'on' : ''}`}
          disabled={!!progressoFerragens}
          onClick={() => void viewerRef.current?.setFerragensVisiveis(!ferragensVisiveis)}>
          {progressoFerragens
            ? `⏳ Carregando ferragens… ${progressoFerragens[0] + 1} de ${progressoFerragens[1]}`
            : `🔩 ${ferragensVisiveis ? 'Ocultar ferragens' : 'Mostrar ferragens'}`}
        </button>

        <hr />

        <input type="search" placeholder="Buscar peça por nome ou id…" value={filtro}
          onChange={(e) => setFiltro(e.target.value)} autoComplete="off" />
        <div className="muted small">Peças por móvel (clique para marcar):</div>
        <div className="vw-list">
          {gruposFiltrados.map((g) => (
            <div key={g.movel_id}>
              <button type="button" className={`cli-item ${aberto === g.movel_id ? 'on' : ''}`}
                onClick={() => setAberto(aberto === g.movel_id ? null : g.movel_id)}>
                <span className="cli-nome">{aberto === g.movel_id ? '▾' : '▸'} {g.nome}</span>
                <span className="cli-count">{g.pieces.length}</span>
              </button>
              {aberto === g.movel_id && g.pieces.map((p) => (
                <button key={p.id} type="button" className={`vw-pid ${selectedId === p.id ? 'on' : ''}`}
                  onClick={() => viewerRef.current?.selectId(p.id)}>
                  <span>{p.id}</span>
                  <span className="muted small">{p.nome}</span>
                </button>
              ))}
            </div>
          ))}
          {grupos.length === 0 && <div className="muted small">Carregando as peças…</div>}
        </div>
      </aside>

      <div className="vw-view">
        <div ref={canvasRef} className="vw-canvas" />
        {debug && <PainelDesempenho viewer={viewerDebug} />}

        {!primeiroChegou && (
          <div className="center-msg">
            <div className="spinner" />
            <div className="muted">
              {estado.status === 'lista' ? 'Buscando os móveis do cliente…'
                : estado.status === 'planta' ? 'Carregando a planta baixa…'
                : 'Carregando o primeiro móvel…'}
            </div>
          </div>
        )}

        {estado.status === 'montando' && primeiroChegou && (
          <div className="pj-load" role="status" aria-live="polite">
            <div className="pj-load-head"><span className="spinner pequeno" /><b>Carregando os outros móveis…</b></div>
            <div className="pj-load-txt">{estado.carregados} de {estado.total} · {estado.atual}</div>
            <div className="pj-bar"><div className="pj-bar-fill" style={{ width: `${progresso}%` }} /></div>
          </div>
        )}

        {estado.status === 'pronto' && (
          <div className="pj-pronto">
            ✓ {estado.total} {estado.total === 1 ? 'móvel carregado' : 'móveis carregados'}
            {estado.falhas.length > 0 && <span className="muted small"> · {estado.falhas.length} falharam</span>}
          </div>
        )}

        {info && info.kind === 'piece' && !medindo && (
          <div className="vw-info">
            <b>Peça {info.id}</b>{pecaSel?.nome ? ` — ${pecaSel.nome}` : ''}<br />
            C×L×E: {dimsPecaReal || info.dims}
            {dimsPecaReal && <span className="muted small"> (medida do projeto)</span>}
            {pecaSel?.materialTexto && <><br />Material: {pecaSel.materialTexto}</>}
            {pecaSel?.movel && <><br /><span className="muted small">Móvel: {pecaSel.movel}</span></>}
            <ListaFolgas
              folgas={folgas}
              nomeDe={(id) => pecaPorId.get(id)?.nome || ''}
              onEscolher={(id) => viewerRef.current?.selectId(id)}
            />
            <div className="vw-info-acoes">
              <button type="button" className="vw-info-ocultar" onClick={ocultarSelecionada}>🙈 Ocultar peça</button>
            </div>
          </div>
        )}

        {medindo && <BarraMedir viewer={viewerRef.current} estado={estadoMedir} onConcluir={alternarMedir} />}
        {medindo && <BussolaEixos eixos={eixosTela} travado={estadoMedir.eixo} />}

        {modalAdmin}

        {pieceMenu && (
          <div ref={ctxRef} className="vw-ctx" style={{ left: pieceMenu.x, top: pieceMenu.y }}>
            <div className="vw-ctx-nome">Peça {pieceMenu.pieceId}</div>
            <button type="button" className="vw-ctx-btn" onClick={ocultarPecaMenu}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                <line x1="1" y1="1" x2="23" y2="23" />
              </svg>
              Ocultar peça
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
